import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface QuizProps {
  questions: QuizQuestion[];
  title?: string;
  quizId?: string;
}

export function Quiz({ questions, title = "Test Your Knowledge", quizId }: QuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = async () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate score
      const finalScore = selectedAnswers.reduce((acc, answer, index) => {
        return answer === questions[index].correctAnswer ? acc + 1 : acc;
      }, 0);
      setScore(finalScore);
      setShowResults(true);
      
      // Track quiz completion
      if (quizId) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          
          // Types will be regenerated automatically after migration
          await (supabase as any).from('quiz_attempts').insert({
            quiz_id: quizId,
            user_id: user?.id || null,
            score: finalScore,
            total_questions: questions.length,
            answers: selectedAnswers,
            session_id: sessionStorage.getItem('quiz_session_id') || crypto.randomUUID(),
            user_agent: navigator.userAgent,
          });
        } catch (error) {
          console.error('Failed to track quiz attempt:', error);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswers([]);
    setShowResults(false);
    setScore(0);
  };

  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    
    return (
      <Card className="mt-12">
        <CardHeader>
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>Quiz Results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="mb-6">
              <p className="text-4xl font-bold text-primary mb-2">
                {score} / {questions.length}
              </p>
              <p className="text-lg text-muted-foreground">
                You scored {percentage}%
              </p>
            </div>
            
            {/* Review Answers */}
            <div className="space-y-6 text-left">
              <h3 className="text-xl font-semibold mb-4">Review Your Answers</h3>
              {questions.map((q, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    {selectedAnswers[index] === q.correctAnswer ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    )}
                    <p className="font-medium">
                      {index + 1}. {q.question}
                    </p>
                  </div>
                  <div className="ml-7 space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Your answer:</span>{" "}
                      <span className={selectedAnswers[index] === q.correctAnswer ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                        {q.options[selectedAnswers[index]]}
                      </span>
                    </p>
                    {selectedAnswers[index] !== q.correctAnswer && (
                      <p className="text-sm">
                        <span className="font-medium">Correct answer:</span>{" "}
                        <span className="text-green-600 dark:text-green-400">
                          {q.options[q.correctAnswer]}
                        </span>
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground italic">
                      {q.explanation}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Button onClick={handleRestart} className="mt-6" size="lg">
              Retake Quiz
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const question = questions[currentQuestion];
  const isAnswered = selectedAnswers[currentQuestion] !== undefined;

  return (
    <Card className="mt-12">
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <Badge variant="secondary">
            Question {currentQuestion + 1} of {questions.length}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {selectedAnswers.filter(a => a !== undefined).length} / {questions.length} answered
          </span>
        </div>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription className="text-base pt-2">{question.question}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mb-6">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedAnswers[currentQuestion] === index
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50 hover:bg-accent/50"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                    selectedAnswers[currentQuestion] === index
                      ? "border-primary bg-primary"
                      : "border-muted-foreground"
                  }`}
                >
                  {selectedAnswers[currentQuestion] === index && (
                    <div className="h-2 w-2 rounded-full bg-white" />
                  )}
                </div>
                <span className="text-sm font-medium">{option}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            Previous
          </Button>
          <Button onClick={handleNext} disabled={!isAnswered}>
            {currentQuestion === questions.length - 1 ? "Finish Quiz" : "Next Question"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
